import { useState, useEffect, type FormEvent } from 'react';
import { Modal } from './Modal';
import type { BoardSettings, BoardSettingsUpdate } from '../../types/board';

interface BoardSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (settings: BoardSettingsUpdate) => void;
  settings: BoardSettings;
  isLoading?: boolean;
}

type SettingsTab = 'general' | 'voting' | 'access';

const SETTINGS_TABS: { id: SettingsTab; label: string }[] = [
  { id: 'general', label: 'General' },
  { id: 'voting', label: 'Voting' },
  { id: 'access', label: 'Access' },
];

export function BoardSettingsModal({
  isOpen,
  onClose,
  onSave,
  settings,
  isLoading = false,
}: BoardSettingsModalProps): JSX.Element {
  const [formData, setFormData] = useState<BoardSettings>(settings);
  const [boardPassword, setBoardPassword] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');

  useEffect(() => {
    setFormData(settings);
    setBoardPassword('');
    setAdminPassword('');
    setActiveTab('general');
  }, [settings, isOpen]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!isLoading) {
      const settingsUpdate: BoardSettingsUpdate = { ...formData };
      if (boardPassword.trim() !== '') {
        settingsUpdate.password = boardPassword;
      }
      if (adminPassword.trim() !== '') {
        settingsUpdate.adminPassword = adminPassword;
      }
      onSave(settingsUpdate);
    }
  };

  const handleChange = <K extends keyof BoardSettings>(
    key: K,
    value: BoardSettings[K]
  ) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Board Settings" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          {SETTINGS_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`border-b-2 px-4 py-2 text-sm font-semibold transition ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-700 dark:border-blue-400 dark:text-blue-300'
                  : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'general' && (
          <div className="space-y-6">
            {/* Card Visibility */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Card Visibility
              </label>
              <select
                value={formData.cardVisibility}
                onChange={(e) =>
                  handleChange('cardVisibility', e.target.value as BoardSettings['cardVisibility'])
                }
                disabled={isLoading}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="VISIBLE">Visible to everyone</option>
                <option value="BLURRED">Blurred until reveal</option>
              </select>
            </div>

            {/* Author Display */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Show author to participants
                </label>
                <input
                  type="checkbox"
                  checked={formData.showAuthorForParticipants}
                  onChange={(e) => handleChange('showAuthorForParticipants', e.target.checked)}
                  disabled={isLoading}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Show author to admin
                </label>
                <input
                  type="checkbox"
                  checked={formData.showAuthorForAdmin}
                  onChange={(e) => handleChange('showAuthorForAdmin', e.target.checked)}
                  disabled={isLoading}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Show card date
                </label>
                <input
                  type="checkbox"
                  checked={formData.showCardDate}
                  onChange={(e) => handleChange('showCardDate', e.target.checked)}
                  disabled={isLoading}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Join Requirements */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Require name on join
                </label>
                <input
                  type="checkbox"
                  checked={formData.requireNameOnJoin}
                  onChange={(e) => handleChange('requireNameOnJoin', e.target.checked)}
                  disabled={isLoading}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Allow anonymous participation
                </label>
                <input
                  type="checkbox"
                  checked={formData.allowAnonymous}
                  onChange={(e) => handleChange('allowAnonymous', e.target.checked)}
                  disabled={isLoading}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Features */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Enable card merging
                </label>
                <input
                  type="checkbox"
                  checked={formData.enableMergeCards}
                  onChange={(e) => handleChange('enableMergeCards', e.target.checked)}
                  disabled={isLoading}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Enable card groups
                </label>
                <input
                  type="checkbox"
                  checked={formData.enableCardGroups}
                  onChange={(e) => handleChange('enableCardGroups', e.target.checked)}
                  disabled={isLoading}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Limits */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Max Columns
                </label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={formData.maxColumns}
                  onChange={(e) => handleChange('maxColumns', parseInt(e.target.value) || 1)}
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Max Cards Per Column
                </label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={formData.maxCardsPerColumn}
                  onChange={(e) =>
                    handleChange('maxCardsPerColumn', parseInt(e.target.value) || 1)
                  }
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'voting' && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <label
                htmlFor="votingEnabled"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Enable voting
              </label>
              <input
                id="votingEnabled"
                type="checkbox"
                checked={formData.votingEnabled}
                onChange={(e) => handleChange('votingEnabled', e.target.checked)}
                disabled={isLoading}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
            </div>

            <div>
              <label
                htmlFor="votesPerParticipant"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Votes per participant
              </label>
              <input
                id="votesPerParticipant"
                type="number"
                min={1}
                max={100}
                value={formData.votesPerParticipant}
                onChange={(e) =>
                  handleChange('votesPerParticipant', parseInt(e.target.value) || 1)
                }
                disabled={isLoading || !formData.votingEnabled}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
              />
            </div>

            <div className="flex items-center justify-between">
              <label
                htmlFor="allowMultipleVotesPerCard"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Allow multiple votes on one card
              </label>
              <input
                id="allowMultipleVotesPerCard"
                type="checkbox"
                checked={formData.allowMultipleVotesPerCard}
                onChange={(e) => handleChange('allowMultipleVotesPerCard', e.target.checked)}
                disabled={isLoading || !formData.votingEnabled}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-60"
              />
            </div>

            <div className="flex items-center justify-between">
              <label
                htmlFor="showVotesToParticipants"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Show vote counts to participants
              </label>
              <input
                id="showVotesToParticipants"
                type="checkbox"
                checked={formData.showVotesToParticipants}
                onChange={(e) => handleChange('showVotesToParticipants', e.target.checked)}
                disabled={isLoading || !formData.votingEnabled}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-60"
              />
            </div>
          </div>
        )}

        {/* Passwords */}
        {activeTab === 'access' && (
        <div className="space-y-4">
          <div>
            <label
              htmlFor="boardPassword"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Board password
            </label>
            <input
              id="boardPassword"
              type="password"
              value={boardPassword}
              onChange={(e) => setBoardPassword(e.target.value)}
              placeholder="Set board password"
              disabled={isLoading}
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Leave blank to keep the current board password.
            </p>
          </div>

          <div>
            <label
              htmlFor="adminPassword"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Admin password
            </label>
            <input
              id="adminPassword"
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              placeholder="Set admin password"
              disabled={isLoading}
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Participants can use this password to become the board admin.
            </p>
          </div>
        </div>
        )}

        {/* Actions */}
        <div className="sticky -bottom-5 flex gap-3 border-t border-gray-200 bg-white pt-4 pb-1 dark:border-gray-700 dark:bg-gray-800 sm:-bottom-6">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : (
              <>
                <span className="sm:hidden">Save</span>
                <span className="hidden sm:inline">Save Settings</span>
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
